// @ts-check
// Relações ONE_TO_MANY mostradas como lista de filhos: chips no card do kanban e tabela na página
// do registro (spec 04, oportunidade por família). Funções puras sobre os metadados.
import { FIELD_TYPE, isFieldDisplayable, isManyToOneRelation, isOneToManyRelation, toPascalCase } from './field-metadata.js';

/**
 * @typedef {import('./field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('./field-metadata.js').ObjectMetadata} ObjectMetadata
 */

/**
 * @param {FieldMetadata} field
 */
export function isFieldDisplayedAsTable(field) {
  return isOneToManyRelation(field) && field.settings?.displayAsTable === true;
}

/**
 * Lado MANY_TO_ONE do filho que aponta para o pai (ex.: opportunityStudent.enrollmentOpportunity).
 * @param {FieldMetadata} field relação ONE_TO_MANY do pai
 * @param {ObjectMetadata | undefined} targetObject
 */
export function getInverseRelationField(field, targetObject) {
  return targetObject?.fields.find((candidate) => candidate.name === field.relation?.targetFieldName && isManyToOneRelation(candidate));
}

/**
 * Relação que identifica o filho para o usuário: a primeira MANY_TO_ONE que não é o pai
 * (ex.: o aluno de opportunityStudent). O chip do card mostra o nome desse registro.
 * @param {ObjectMetadata | undefined} targetObject
 * @param {string | undefined} inverseFieldName
 */
export function getChildIdentifyingRelationField(targetObject, inverseFieldName) {
  return targetObject?.fields.find(
    (candidate) => isManyToOneRelation(candidate) && candidate.name !== inverseFieldName && candidate.isActive !== false && !candidate.isSystem,
  );
}

/**
 * Convenção do motor: o filho tem `<campo>Override` (etapa própria) e `effective<Campo>` (etapa efetiva)
 * para o campo de agrupamento do pai (stage).
 * @param {ObjectMetadata | undefined} targetObject
 * @param {string} parentFieldName
 */
export function getChildStageFields(targetObject, parentFieldName) {
  const overrideField = targetObject?.fields.find((candidate) => candidate.name === `${parentFieldName}Override` && candidate.type === FIELD_TYPE.SELECT);
  const effectiveField = targetObject?.fields.find((candidate) => candidate.name === `effective${toPascalCase(parentFieldName)}` && candidate.type === FIELD_TYPE.SELECT);
  return { overrideField, effectiveField };
}

/**
 * Etapa do filho: a própria quando preenchida, senão a do pai (regra R1); sem campo de etapa própria,
 * usa a etapa efetiva calculada pelo motor.
 * @param {Record<string, any>} child
 * @param {Record<string, any>} parentRecord
 * @param {string} parentFieldName
 * @param {{ overrideField?: FieldMetadata, effectiveField?: FieldMetadata }} stageFields
 * @returns {string | null}
 */
export function getChildStage(child, parentRecord, parentFieldName, { overrideField, effectiveField }) {
  if (overrideField) return child[overrideField.name] ?? parentRecord[parentFieldName] ?? null;
  if (effectiveField) return child[effectiveField.name] ?? null;
  return parentRecord[parentFieldName] ?? null;
}

/**
 * Colunas da tabela de filhos: campos visíveis da view principal do objeto filho, sem o pai,
 * o identificador e outras listas; sem view, os campos de negócio exibíveis.
 * @param {ObjectMetadata} targetObject
 * @param {FieldMetadata | undefined} inverseField
 * @param {{ viewFields: { fieldMetadataId: string, position: number, isVisible: boolean }[] } | null} indexView
 */
export function getChildTableFields(targetObject, inverseField, indexView) {
  const labelFieldId = targetObject.labelIdentifierFieldMetadataId;
  const isColumnCandidate = (/** @type {FieldMetadata | undefined} */ field) =>
    Boolean(field) &&
    isFieldDisplayable(/** @type {FieldMetadata} */ (field)) &&
    field?.id !== inverseField?.id &&
    field?.id !== labelFieldId &&
    !isOneToManyRelation(/** @type {FieldMetadata} */ (field));
  const fieldsById = new Map(targetObject.fields.map((field) => [field.id, field]));
  const fromView = (indexView?.viewFields ?? [])
    .filter((viewField) => viewField.isVisible)
    .sort((first, second) => first.position - second.position)
    .map((viewField) => fieldsById.get(viewField.fieldMetadataId))
    .filter(isColumnCandidate);
  if (fromView.length > 0) return /** @type {FieldMetadata[]} */ (fromView);
  return targetObject.fields
    .filter((field) => isColumnCandidate(field) && !field.isSystem && !['createdAt', 'updatedAt', 'createdBy'].includes(field.name))
    .sort((first, second) => (first.position ?? 0) - (second.position ?? 0));
}

// Colunas emprestadas do registro identificado pelo filho: a tabela de alunos do card mostra e edita
// dados da pessoa (nascimento e escola de origem) sem abrir a página dela.
const LOOKUP_FIELDS_BY_OBJECT = /** @type {Record<string, Array<{ name: string, shortLabelKey?: string }>>} */ ({
  opportunityStudent: [{ name: 'birthDate', shortLabelKey: 'relatedRecords.shortLabels.birthDate' }, { name: 'originSchool' }],
});

/**
 * @typedef {{ id: string, field: FieldMetadata, relationField: FieldMetadata, shortLabelKey?: string }} LookupColumn
 */

/**
 * @param {ObjectMetadata} targetObject filho (ex.: opportunityStudent)
 * @param {FieldMetadata | undefined} identifyingField relação que identifica o filho (ex.: student)
 * @param {ObjectMetadata | undefined} identifiedObject objeto apontado por ela (ex.: person)
 * @returns {LookupColumn[]}
 */
export function getChildLookupColumns(targetObject, identifyingField, identifiedObject) {
  if (!identifyingField || !identifiedObject) return [];
  /** @type {LookupColumn[]} */
  const columns = [];
  for (const lookup of LOOKUP_FIELDS_BY_OBJECT[targetObject.nameSingular] ?? []) {
    const field = identifiedObject.fields.find((candidate) => candidate.name === lookup.name && candidate.isActive !== false);
    if (field) columns.push({ id: `lookup:${field.id}`, field, relationField: identifyingField, shortLabelKey: lookup.shortLabelKey });
  }
  return columns;
}
